import React, { useCallback, useContext, useEffect, useState } from "react";
import InnerLayout from "../../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DynamicTransactionTable from "../../../components/tables/dynamic-transaction-table";
import LoadingSpinner from "../../../components/helper-components/loading-spinner";
import { DataContext } from "../../../components/layouts/main-layout";
import moment from "moment";
import { eInvoiceTableAction } from "../../../redux/actions/sales-action";
import FilterRadioButtons from "../../../components/forms/filter-radio-buttons";
import EInvoiceStatusBadge from "../../../components/sales-section/e-invoice/e-invoice-status-badge";
import { getEInvoiceList, getRowId } from "../../../components/sales-section/e-invoice/e-invoice-utils";
import CancelEInvoiceModal from "../../../components/sales-section/e-invoice/cancel-e-invoice-modal";
import { FileText, XCircle } from "lucide-react";

const EInvoiceFilter = ({ filterData, setFilterData, setCurrentPage }) => {
    const handleChange = (e) => {
        setFilterData({ ...filterData, orderStatus: e.target.value });
        setCurrentPage(1);
    };
    return (
        <div
            style={{
                minWidth: "13rem",
                zIndex: 999,
                top: "40px",
                right: "-5px",
                boxShadow: "0px 1px 3px 0px var(--popover-shadow-color)",
            }}
            className="absolute border border-[var(--popover-border-color)] py-2 px-2 bg-[var(--popover-bg-color)] rounded z-10 flex flex-col"
        >
            <span className="text-[length:--popover-heading-font-size] font-[var(--popover-heading-font-weight)] text-[var(--popover-heading-fg-color)] whitespace-nowrap px-[8px] py-[12px]">
                FILTER BY
            </span>
            <hr className="border border-EAECF0" />
            <div className="px-5">
                <FilterRadioButtons
                    name="einvoice_status"
                    label="Status"
                    options={[
                        { label: "Generated", value: "generated" },
                        { label: "Cancelled", value: "cancelled" }
                    ]}
                    handleChange={handleChange}
                    value={filterData?.orderStatus}
                />
            </div>
        </div>
    );
};


const EInvoiceTable = () => {
    const { permissionFn } = useContext(DataContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [eInvoices, setEInvoices] = useState([]);
    const [dataCount, setDataCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [reload, setReload] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [cancelRow, setCancelRow] = useState(null);

    const [filterData, setFilterData] = useState({
        sort_by: "",
        sort_order: "",
        selectColumn: "",
        from: "",
        to: "",
        inputValue: "",
        orderStatus: "",
    });

    useEffect(() => {
        dispatch(
            eInvoiceTableAction(
                {
                    pageNumber: currentPage,
                    pageSize: pageSize,
                    filterData,
                },
                onSuccess,
                onError
            )
        );
    }, [currentPage, pageSize, filterData, reload]);

    const onSuccess = (data) => {
        setError(false);
        setLoading(false);
        const list = getEInvoiceList(data);
        const count =
            data?.data?.totalCount ??
            data?.totalCount ??
            list.length;
        setEInvoices(list);
        setDataCount(count);
    };

    const onError = () => {
        setError(true);
        setLoading(false);
    };

    const paginationProps = {
        pageSize,
        setPageSize,
        dataCount,
        setCurrentPage,
        currentPage,
    };

    const handleFilterClick = useCallback(() => {
        setShowFilter((prev) => !prev);
    }, []);

    const filterby = showFilter ? (
        <EInvoiceFilter
            filterData={filterData}
            setFilterData={setFilterData}
            setCurrentPage={setCurrentPage}
        />
    ) : null;

    const tableBodyData = eInvoices?.map((item) => {
        const inv  = item?.sales_order ?? item;
        const einv = item?.e_invoice ?? item;
        const einvStatus = item?.eInvoiceStatus ?? {};

        const invNo = inv?.soit_invoice_number ?? einv?.invoice_number ?? "--";
        const irn = einvStatus?.irn ?? item?.irnDetails?.irn ?? einv?.irn ?? einv?.Irn ?? null;

        const rawDate = inv?.soit_order_date ?? inv?.created_date;
        const hasActiveEwb = Boolean(item?.irnDetails?.ewbno) && item?.irnDetails?.is_eway_cancelled !== true;

        return {
            id: getRowId(item),
            soit_order_date: rawDate && moment(rawDate).isValid()
                ? moment(rawDate).format("MMM D, YYYY")
                : "--",
            soit_invoice_number: (
                <span className="fw_500 cursor-pointer">{invNo}</span>
            ),
            cmr_name:  inv?.cmr_name  ?? item?.cmr_name  ?? "--",
            irn_number: irn ? (
                <span className="fw_500 block truncate max-w-[260px]" title={irn}>{irn}</span>
            ) : (
                <span className="text-[var(--placeholder-color)]">--</span>
            ),
            soit_total_amount: "₹ " + (inv?.soit_total_amount ?? item?.soit_total_amount ?? "0.00"),
            order_status: <EInvoiceStatusBadge status={einvStatus?.status} />,
            /* drives the row menu (actionsPermissions) – e-Invoice status, not the sales order status */
            soit_order_status: einvStatus?.status ?? "",
            can_cancel: einvStatus?.can_cancel === true,
            /* only one active e-way bill per IRN */
            can_generate_ewb: einvStatus?.status === "GENERATED" && !hasActiveEwb,
        };
    });

    const findRow = (id) => eInvoices.find((row) => getRowId(row) === id) ?? null;

    const rowMenuItems = (rowData) => [
        ...(rowData?.can_generate_ewb
            ? [{
                label: "Generate E-Way",
                icon: <FileText size={14} strokeWidth={2} />,
                onClick: (id) =>
                    navigate(`/generate-e-way-bill-irn/${id}`, { state: { eInvoice: findRow(id) } }),
            }]
            : []),
        ...(rowData?.can_cancel
            ? [{
                label: "Cancel e-Invoice",
                icon: <XCircle size={14} strokeWidth={2} />,
                danger: true,
                onClick: (id) => setCancelRow(findRow(id)),
            }]
            : []),
    ];

    return (
        <InnerLayout
            permissionFn={permissionFn}
            module="sales_invoice"
            title="e-Invoice"
        >
            {loading ? (
                <LoadingSpinner />
            ) : error ? (
                <></>
            ) : (
                <>
                    <DynamicTransactionTable
                        transaction="e-Invoice"
                        transaction_type="sales_invoice"
                        setInvoiceItems={() => { }}
                        invoiceItems={[]}
                        options={[]}
                        orders={eInvoices}
                        setOrders={setEInvoices}
                        tableHeading1={[]}
                        is_checkbox_req={false}
                        onClickItemFn={(id) =>
                            navigate(`/view-e-invoice/${id}`, {
                                state: { eInvoice: eInvoices.find((row) => getRowId(row) === id) ?? null },
                            })
                        }
                        list={false}
                        permissionFn={permissionFn}
                        module={"sales_invoice"}
                        path="sales-invoice"
                        reload={reload}
                        setReload={setReload}
                        loading={loading}
                        setLoading={setLoading}
                        deleteAction={() => { }}
                        label="e-Invoice"
                        showFilter={showFilter}
                        filterFn={handleFilterClick}
                        setFilterData={setFilterData}
                        filterData={filterData}
                        paginationProps={paginationProps}
                        tableHeading={tableHeading}
                        tableBodyData={tableBodyData}
                        placeholder="Search (/)"
                        filterBy={filterby}
                        initialFilter={{
                            sort_by: "",
                            sort_order: "",
                            selectColumn: "soit_invoice_number",
                            from: "",
                            to: "",
                            inputValue: "",
                            orderStatus: "",
                        }}
                        handleSelect={() => { }}
                        transactionKey="soit"
                        actionsPermissions={{
                            edit: [],
                            delete: [],
                            print: [],
                            send: [],
                            clone: [],
                        }}
                        rowMenuItems={rowMenuItems}
                        menu={true}
                    />
                    {cancelRow && (
                        <CancelEInvoiceModal
                            row={cancelRow}
                            onClose={() => setCancelRow(null)}
                            onCancelled={() => {
                                setCancelRow(null);
                                setReload((prev) => !prev);
                            }}
                        />
                    )}
                </>
            )}
        </InnerLayout>
    );
};

export default EInvoiceTable;

const tableHeading = [
    {
        label: "DATE",
        value: "soit_order_date",
        display: true,
        column: true,
    },
    {
        label: "TRANSACTION #",
        value: "soit_invoice_number",
        display: true,
        column: true,
    },
    {
        label: "CUSTOMER NAME",
        value: "cmr_name",
        display: true,
        column: true,
    },
    {
        label: "IRN #",
        value: "irn_number",
        display: true,
        column: true,
    },
    {
        label: "TOTAL",
        value: "soit_total_amount",
        display: true,
        column: true,
        className: "justify-end pe-5",
    },
    {
        label: "STATUS",
        value: "order_status",
        display: true,
        column: true,
    },
    {
        label: "",
        value: "actions",
        column: false,
    },
];
